import { create } from 'zustand';
import type { User, Session, Provider } from '@supabase/supabase-js';
import { supabase } from '@/core/supabase';
import { getErrorMessage } from '@/shared/utils/errors';
import { createLogger } from '@/shared/utils/logger';

const authLog = createLogger('AuthStore');

// Module-level variable for auth subscription cleanup
let _authSubscription: { unsubscribe: () => void } | null = null;

/** Supported OAuth providers */
export const OAUTH_PROVIDERS = ['google', 'github'] as const;
export type OAuthProvider = (typeof OAUTH_PROVIDERS)[number];

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  initialized: boolean;

  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, username: string) => Promise<{ error: string | null; needsConfirmation?: boolean }>;
  signOut: () => Promise<void>;
  /** OAuth社交登录 (Google/GitHub) — 重定向到第三方授权页 */
  signInWithOAuth: (provider: OAuthProvider) => Promise<{ error: string | null }>;
  /** 手机号OTP — 发送验证码 */
  sendPhoneOtp: (phone: string) => Promise<{ error: string | null }>;
  /** 手机号OTP — 验证码登录/注册 */
  verifyPhoneOtp: (phone: string, token: string) => Promise<{ error: string | null }>;
  /** 发送密码重置邮件 */
  resetPasswordForEmail: (email: string) => Promise<{ error: string | null }>;
  /** 使用 recovery token 后设置新密码 */
  updatePassword: (newPassword: string) => Promise<{ error: string | null }>;
  /** 重新发送确认邮件 */
  resendConfirmation: (email: string) => Promise<{ error: string | null }>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  loading: false,
  initialized: false,

  initialize: async () => {
    if (get().initialized) return;

    try {
      // Listen for auth state changes
      if (_authSubscription) _authSubscription.unsubscribe();
      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        set({
          session,
          user: session?.user ?? null,
          initialized: true,
        });
      });
      _authSubscription = subscription;

      // Get initial session
      const { data, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr) authLog.warn('Failed to get session', { error: sessionErr.message });
      set({
        session: data.session,
        user: data.session?.user ?? null,
        initialized: true,
      });
    } catch (err) {
      authLog.warn('initialize failed', { error: getErrorMessage(err) });
      // Ensure initialized is set so the app doesn't hang on the loading screen
      set({ initialized: true });
    }
  },

  signIn: async (email, password) => {
    set({ loading: true });
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (!error && data.session) {
        set({ loading: false, session: data.session, user: data.session.user });
      } else {
        set({ loading: false });
      }
      if (error) {
        // Friendly Chinese error messages
        const msg = error.message?.toLowerCase() ?? '';
        if (msg.includes('invalid login credentials')) {
          return { error: '邮箱或密码错误，请检查后重试' };
        }
        if (msg.includes('email not confirmed')) {
          return { error: '邮箱尚未验证，请检查收件箱或使用新邮箱注册' };
        }
        return { error: error.message };
      }
      return { error: null };
    } catch (err) {
      set({ loading: false });
      return { error: getErrorMessage(err) };
    }
  },

  signUp: async (email, password, username) => {
    set({ loading: true });
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { username } },
      });

      // Handle "User already registered" — try signing in directly
      // (covers the case where user registered before email confirmation was disabled)
      if (error?.message?.toLowerCase().includes('already registered')) {
        const { data: signInData, error: signInError } =
          await supabase.auth.signInWithPassword({ email, password });
        if (!signInError && signInData.session) {
          set({ loading: false, session: signInData.session, user: signInData.session.user });
          return { error: null, needsConfirmation: false };
        }
        set({ loading: false });
        return { error: '该邮箱已注册。如果是你的账号，请直接登录；如密码不同，请使用「忘记密码」找回。' };
      }

      if (!error && data.user) {
        // Create profile (ignore conflict if already exists)
        const { error: profileErr } = await supabase.from('profiles').upsert({
          id: data.user.id,
          username,
        }, { onConflict: 'id' });
        if (profileErr) authLog.warn('Profile upsert failed', { error: profileErr.message });
        // Immediately update store (signUp with autoConfirm returns session)
        if (data.session) {
          set({ loading: false, session: data.session, user: data.session.user });
          return { error: null, needsConfirmation: false };
        } else {
          // Email confirmation required — no session yet
          set({ loading: false });
          return { error: null, needsConfirmation: true };
        }
      } else {
        set({ loading: false });
      }
      return { error: error?.message ?? null };
    } catch (err) {
      set({ loading: false });
      return { error: getErrorMessage(err) };
    }
  },

  signOut: async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      authLog.warn('signOut network error', { error: getErrorMessage(err) });
    }
    // Always clear local state regardless of server response
    set({ user: null, session: null });
  },

  signInWithOAuth: async (provider) => {
    set({ loading: true });
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider as Provider,
        options: {
          redirectTo: `${window.location.origin}/workshop`,
        },
      });
      // If no error, browser will redirect — loading state is intentionally kept
      if (error) {
        set({ loading: false });
        const msg = error.message?.toLowerCase() ?? '';
        if (msg.includes('provider is not enabled')) {
          return { error: `${provider} 登录尚未启用，请联系管理员配置` };
        }
        return { error: error.message };
      }
      return { error: null };
    } catch (err) {
      set({ loading: false });
      return { error: getErrorMessage(err) };
    }
  },

  sendPhoneOtp: async (phone) => {
    set({ loading: true });
    try {
      const { error } = await supabase.auth.signInWithOtp({ phone });
      set({ loading: false });
      if (error) {
        const msg = error.message?.toLowerCase() ?? '';
        if (msg.includes('phone provider is not enabled')) {
          return { error: '手机号登录尚未启用，请联系管理员配置' };
        }
        if (msg.includes('rate limit')) {
          return { error: '发送过于频繁，请稍后再试' };
        }
        return { error: error.message };
      }
      return { error: null };
    } catch (err) {
      set({ loading: false });
      return { error: getErrorMessage(err) };
    }
  },

  verifyPhoneOtp: async (phone, token) => {
    set({ loading: true });
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        phone,
        token,
        type: 'sms',
      });
      if (!error && data.session) {
        set({ loading: false, session: data.session, user: data.session.user });
      } else {
        set({ loading: false });
      }
      if (error) {
        const msg = error.message?.toLowerCase() ?? '';
        if (msg.includes('token has expired') || msg.includes('otp expired')) {
          return { error: '验证码已过期，请重新获取' };
        }
        if (msg.includes('invalid') || msg.includes('incorrect')) {
          return { error: '验证码错误，请检查后重试' };
        }
        return { error: error.message };
      }
      return { error: null };
    } catch (err) {
      set({ loading: false });
      return { error: getErrorMessage(err) };
    }
  },

  resetPasswordForEmail: async (email) => {
    set({ loading: true });
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      set({ loading: false });
      return { error: error?.message ?? null };
    } catch (err) {
      set({ loading: false });
      return { error: getErrorMessage(err) };
    }
  },

  updatePassword: async (newPassword) => {
    set({ loading: true });
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      set({ loading: false });
      return { error: error?.message ?? null };
    } catch (err) {
      set({ loading: false });
      return { error: getErrorMessage(err) };
    }
  },

  resendConfirmation: async (email) => {
    set({ loading: true });
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });
      set({ loading: false });
      return { error: error?.message ?? null };
    } catch (err) {
      set({ loading: false });
      return { error: getErrorMessage(err) };
    }
  },
}));
